import { __, sprintf } from '../i18n/vsme';
import type { MissionDocumentRow, MissionDocumentType } from './missionStartApi';

type DocumentFieldBase = {
  type: MissionDocumentType;
  titleBase: string;
  description: string;
  placeholder: string;
};

const DOCUMENT_FIELD_BASES: DocumentFieldBase[] = [
  {
    type: 'letter_of_mission',
    titleBase: __('Lettre de mission'),
    description: __('Déposer la lettre de mission signée par toutes les parties.'),
    placeholder: __('Déposer la lettre de mission'),
  },
  {
    type: 'acceptance_questionnaire',
    titleBase: __('Questionnaire d’acceptation et de maintien de la mission'),
    description: __('Déposer le questionnaire d’acceptation et de maintien de la mission complété.'),
    placeholder: __('Déposer le questionnaire d’acceptation'),
  },
  {
    type: 'aml_questionnaire',
    titleBase: __('Questionnaire de lutte anti-blanchiment'),
    description: __('Déposer le questionnaire de lutte anti-blanchiment complété.'),
    placeholder: __('Déposer le questionnaire anti-blanchiment'),
  },
];

export type MissionDocumentFieldConfig = DocumentFieldBase & {
  title: string;
  required: boolean;
};

export const missionDocumentTypeLabel = (type: MissionDocumentType): string => {
  const base = DOCUMENT_FIELD_BASES.find((field) => field.type === type);
  return base?.titleBase ?? type;
};

export const buildDocumentFieldConfigs = (
  requiredDocumentTypes: MissionDocumentType[],
): MissionDocumentFieldConfig[] => {
  const requiredSet = new Set(requiredDocumentTypes);

  return DOCUMENT_FIELD_BASES.map((field) => {
    const required = requiredSet.has(field.type);
    return {
      ...field,
      required,
      title: required ? `${field.titleBase} (${__('obligatoire')})` : field.titleBase,
    };
  });
};

export const missionAcceptationIsComplete = (
  documents: MissionDocumentRow[],
  requiredDocumentTypes: MissionDocumentType[],
): boolean => {
  if (requiredDocumentTypes.length === 0) {
    return true;
  }

  const uploaded = new Set(documents.map((doc) => doc.document_type));
  return requiredDocumentTypes.every((type) => uploaded.has(type));
};

export const formatMissingMissionDocuments = (missing: MissionDocumentType[]): string => {
  if (missing.length === 0) {
    return __('Les documents obligatoires de la mission doivent être déposés.');
  }

  const labels = missing.map((type) => missionDocumentTypeLabel(type));
  return sprintf(__('Documents obligatoires manquants : %s'), labels.join(', '));
};

export const acceptationStepValidationMessage = (
  requiredDocumentTypes: MissionDocumentType[],
): string => {
  if (requiredDocumentTypes.length === 0) {
    return __('Vous pouvez continuer sans déposer de document.');
  }

  const labels = requiredDocumentTypes.map((type) => missionDocumentTypeLabel(type));
  return sprintf(__('Veuillez déposer : %s'), labels.join(', '));
};
